import clsx from "clsx";
import PropTypes from "prop-types";
import * as React from "react";
import { resolveInteractiveElement } from "../../utils/resolveElement";
import css from "./Card.module.css";

const ALIGN_MAP = {
  start: "flex-start",
  end: "flex-end",
  "space-between": "space-between",
};

const CardRoot = React.forwardRef(
  (
    {
      variant = "elevated",
      as,
      clickable = false,
      href,
      className,
      children,
      ...rest
    },
    ref,
  ) => {
    const Component = resolveInteractiveElement({ as, href, clickable });
    const isInteractive = clickable || Boolean(href) || Component === "a";

    const interactiveProps = isInteractive
      ? {
          ...(Component === "button" ? { type: "button" } : {}),
          ...(href ? { href } : {}),
        }
      : {};

    return (
      <Component
        ref={ref}
        className={clsx(
          css.root,
          css[variant],
          isInteractive && css.clickable,
          className,
        )}
        {...interactiveProps}
        {...rest}
      >
        {children}
      </Component>
    );
  },
);

CardRoot.displayName = "CardRoot";

CardRoot.propTypes = {
  variant: PropTypes.oneOf(["elevated", "filled", "outlined"]),
  as: PropTypes.oneOf(["div", "article", "a", "button"]),
  clickable: PropTypes.bool,
  href: PropTypes.string,
  className: PropTypes.string,
  children: PropTypes.node,
};

const CardMedia = React.forwardRef(({ className, children, ...rest }, ref) => (
  <div ref={ref} className={clsx(css.media, className)} {...rest}>
    {children}
  </div>
));

CardMedia.displayName = "CardMedia";

CardMedia.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

const CardHeader = React.forwardRef(
  ({ title, subtitle, avatar, action, className, ...rest }, ref) => (
    <div ref={ref} className={clsx(css.header, className)} {...rest}>
      {avatar && <div className={css.avatar}>{avatar}</div>}
      <div className={css.headerText}>
        {title && <div className={css.title}>{title}</div>}
        {subtitle && <div className={css.subtitle}>{subtitle}</div>}
      </div>
      {action && <div className={css.headerAction}>{action}</div>}
    </div>
  ),
);

CardHeader.displayName = "CardHeader";

CardHeader.propTypes = {
  title: PropTypes.node,
  subtitle: PropTypes.node,
  avatar: PropTypes.node,
  action: PropTypes.node,
  className: PropTypes.string,
};

const CardContent = React.forwardRef(
  ({ className, children, ...rest }, ref) => (
    <div ref={ref} className={clsx(css.content, className)} {...rest}>
      {children}
    </div>
  ),
);

CardContent.displayName = "CardContent";

CardContent.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

const CardActions = React.forwardRef(
  ({ align = "end", className, children, ...rest }, ref) => (
    <div
      ref={ref}
      className={clsx(css.actions, className)}
      style={{ justifyContent: ALIGN_MAP[align] }}
      {...rest}
    >
      {children}
    </div>
  ),
);

CardActions.displayName = "CardActions";

CardActions.propTypes = {
  align: PropTypes.oneOf(["start", "end", "space-between"]),
  className: PropTypes.string,
  children: PropTypes.node,
};

const Card = {
  Root: CardRoot,
  Media: CardMedia,
  Header: CardHeader,
  Content: CardContent,
  Actions: CardActions,
};

export default Card;
